import { SubscriptionTypes } from '../../Business/detail/actions/epgRecordingActions/SubscriptionSettings.types';
import { Backend } from '../Backend';
import { IRecordingBase, ISubscription, Recording, RecordingListResponse } from './RecordingApi.types';

export class RecordingApi {
  /**
   * Retrieve
   */
  static DeviceRecordingsAndSubscriptions(backend: Backend, deviceId: string): Promise<RecordingListResponse> {
    const url = backend.makeUrl(`recording/v1/device/${encodeURIComponent(deviceId)}`);
    return backend.getJson(url, backend.bearer) as Promise<RecordingListResponse>;
  }

  static createSubscription(
    backend: Backend,
    deviceId: string,
    assetId: string,
    channelId: string,
    subscriptionType: SubscriptionTypes,
    recordingOptions: { beginBuffer: number; endBuffer: number; deleteProtected: boolean }
  ): Promise<unknown> {
    const dId = encodeURIComponent(deviceId);
    const pId = encodeURIComponent(assetId);
    const cId = encodeURIComponent(channelId);
    const url = backend.makeUrl(
      `recording/v1/device/${dId}/subscription/${this._getSubscriptionTypeString(
        subscriptionType
      )}/${pId}/channel/${cId}`
    );
    return backend.postJson(url, recordingOptions, backend.bearer);
  }

  static deleteRecording(backend: Backend, recordingId: string, deviceId: string): Promise<unknown> {
    const dId = encodeURIComponent(deviceId);
    const rId = encodeURIComponent(recordingId);
    const url = backend.makeUrl(`recording/v1/device/${dId}/recording/${rId}`);
    return backend.delete(url, backend.bearer);
  }

  static editRecording(backend: Backend, recording: IRecordingBase): Promise<unknown> {
    const dId = encodeURIComponent(recording.device);
    const rId = encodeURIComponent(recording.identifier);
    const url = backend.makeUrl(`recording/v1/device/${dId}/recording/${rId}`);
    return backend.putJson(url, recording, backend.bearer);
  }

  /**
   * @private
   */
  static _getSubscriptionTypeString(type: SubscriptionTypes) {
    switch (type) {
      case SubscriptionTypes.PROGRAM:
        return 'program';
      case SubscriptionTypes.SEASON:
        return 'season';
      case SubscriptionTypes.SERIES:
        return 'series';
    }
  }

  static editSubscription(backend: Backend, device: string, subscription: ISubscription, type: SubscriptionTypes) {
    const dId = encodeURIComponent(device);
    const sId = encodeURIComponent(subscription.identifier);
    const url = backend.makeUrl(
      `recording/v1/device/${dId}/subscription/${this._getSubscriptionTypeString(type)}/${sId}`
    );
    return backend.putJson(url, subscription, backend.bearer);
  }

  static deleteSubscription(backend: Backend, recording: Recording, type: SubscriptionTypes) {
    const dId = encodeURIComponent(recording.device);
    let id;
    if (type === SubscriptionTypes.SERIES) id = encodeURIComponent(recording.series!);
    else if (type === SubscriptionTypes.SEASON) id = encodeURIComponent(recording.season!);
    else if (type === SubscriptionTypes.PROGRAM) id = encodeURIComponent(recording.program);
    const cId = encodeURIComponent(recording.channel);
    const url = backend.makeUrl(
      `recording/v1/device/${dId}/subscription/${this._getSubscriptionTypeString(type)}/${id}/channel/${cId}`
    );
    return backend.delete(url, backend.bearer);
  }

  static deleteSeriesSubscription(backend: Backend, seriesId: string, channelId: string) {
    const dId = encodeURIComponent(backend.sessionInfo!.npvr!.id);
    const id = encodeURIComponent(seriesId);
    const cId = encodeURIComponent(channelId);
    const url = backend.makeUrl(
      `recording/v1/device/${dId}/subscription/${SubscriptionTypes.SERIES}/${id}/channel/${cId}`
    );
    return backend.delete(url, backend.bearer);
  }

  static deleteRecordings(backend: Backend, deviceId: string, seriesId: string, channelId: string) {
    const dId = encodeURIComponent(deviceId);
    const params = { series: seriesId, channels: channelId, states: ['RECORDING', 'RECORDED'] };
    const url = backend.makeUrl(`recording/v1/device/${dId}/recording`, params);
    return backend.delete(url, backend.bearer);
  }
}
